import React from 'react';
import QRCode from 'qrcode';
import { jsPDF } from 'jspdf';

interface PatientQRCodePDFProps {
  patientId: string;
  patientName: string;
  qrCodeUrl: string;
  genericQrCodeUrl: string;
  onGenerate?: () => void;
}

export const PatientQRCodePDF: React.FC<PatientQRCodePDFProps> = ({
  patientId,
  patientName,
  qrCodeUrl,
  genericQrCodeUrl,
  onGenerate
}) => {
  const generatePDF = async () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
      });
      
      // Set font sizes and styles
      const titleFontSize = 18;
      const subtitleFontSize = 14;
      const normalFontSize = 12;
      const smallFontSize = 10;
      
      // Page dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Generate QR codes as data URLs
      const registrationQrCodeDataUrl = await QRCode.toDataURL(qrCodeUrl, {
        width: 150,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      const genericQrCodeDataUrl = await QRCode.toDataURL(genericQrCodeUrl, {
        width: 150,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      // Add colored header background
      doc.setFillColor(76, 175, 80); // Green color
      doc.rect(0, 0, pageWidth, 30, 'F');
      
      // Add header with logo and title
      doc.setTextColor(255, 255, 255); // White text
      doc.setFontSize(titleFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('Magnolia Health and Wellness', pageWidth / 2, margin - 5, { align: 'center' });
      
      // Add patient name
      doc.setTextColor(255, 255, 255); // Keep white text for subtitle in header
      doc.setFontSize(subtitleFontSize);
      doc.text(`Mobile App Registration for: ${patientName}`, pageWidth / 2, margin + 5, { align: 'center' });
      
      // Reset text color for the rest of the document
      doc.setTextColor(0, 0, 0);
      
      // Add QR codes side by side
      const qrCodeWidth = 50;
      const qrCodeHeight = 50;
      const qrCodeY = margin + 20;
      
      // Registration QR code (left)
      const registrationQrX = pageWidth / 4 - qrCodeWidth / 2;
      doc.addImage(registrationQrCodeDataUrl, 'PNG', registrationQrX, qrCodeY, qrCodeWidth, qrCodeHeight);
      
      // Generic QR code (right)
      const genericQrX = (3 * pageWidth) / 4 - qrCodeWidth / 2;
      doc.addImage(genericQrCodeDataUrl, 'PNG', genericQrX, qrCodeY, qrCodeWidth, qrCodeHeight);
      
      // Add QR code labels with consistent styling
      doc.setFontSize(smallFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('Registration QR Code', pageWidth / 4, qrCodeY + qrCodeHeight + 7, { align: 'center' });
      doc.text('App Homepage QR Code', (3 * pageWidth) / 4, qrCodeY + qrCodeHeight + 7, { align: 'center' });
      
      doc.setFontSize(smallFontSize);
      doc.setFont('helvetica', 'italic');
      doc.text('(Use for first-time setup)', pageWidth / 4, qrCodeY + qrCodeHeight + 12, { align: 'center' });
      doc.text('(Use after registration)', (3 * pageWidth) / 4, qrCodeY + qrCodeHeight + 12, { align: 'center' });
      
      // Add registration instructions
      const instructionsY = qrCodeY + qrCodeHeight + 30;
      doc.setFontSize(subtitleFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('Registration Instructions:', margin, instructionsY);
      
      // Use consistent font for all instructions
      doc.setFontSize(normalFontSize);
      doc.setFont('helvetica', 'normal');
      const instructions = [
        '1. Scan the Registration QR Code with your smartphone camera',
        '2. Tap the link that appears',
        '3. Enter your date of birth for verification',
        '4. Enter your email address',
        '5. Create a password for your account',
        '6. Save the app to your home screen (see instructions below)'
      ];
      
      let yPos = instructionsY + 10;
      const lineHeight = 8;
      instructions.forEach(instruction => {
        doc.text(instruction, margin, yPos);
        yPos += lineHeight;
      });
      
      // Add platform-specific instructions in two columns
      yPos += 5;
      doc.setFontSize(subtitleFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('How to Save to Home Screen:', margin, yPos);
      yPos += 10;
      
      // Column settings
      const colWidth = contentWidth / 2 - 5;
      const col1X = margin;
      const col2X = margin + colWidth + 10;
      
      // iOS Instructions (Left Column)
      doc.setFontSize(smallFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('iPhone / iOS:', col1X, yPos);

      // Android Instructions (Right Column)
      doc.setFont('helvetica', 'bold');
      doc.text('Android:', col2X, yPos);
      yPos += 7;
            
      doc.setFont('helvetica', 'normal');
      const iosInstructions = [
        '1. Open the page in Safari',
        '2. Tap the Share icon at the bottom',
        '3. Scroll down and tap "Add to Home Screen"',
        '4. Tap "Add" in the top right corner'
      ];
      
      let iosYPos = yPos;
      iosInstructions.forEach(instruction => {
        doc.text(instruction, col1X, iosYPos);
        iosYPos += 7;
      });
      
            
      doc.setFont('helvetica', 'normal');
      const androidInstructions = [
        '1. Open the page in Chrome',
        '2. Tap the three dots menu in the top right',
        '3. Tap "Add to Home screen"',
        '4. Tap "Add" to confirm'
      ];
      
      let androidYPos = yPos;
      androidInstructions.forEach(instruction => {
        doc.text(instruction, col2X, androidYPos);
        androidYPos += 7;
      });
      
      // Add a divider line
      const dividerY = Math.max(iosYPos, androidYPos) + 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, dividerY, pageWidth - margin, dividerY);
      
      // Add footer with contact information
      const footerY = pageHeight - 20;
      doc.setFillColor(240, 240, 240);
      doc.rect(0, footerY - 15, pageWidth, 20, 'F');
      
      doc.setFontSize(smallFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('For assistance, please contact our office at (530) 399-5600', pageWidth / 2, footerY - 10, { align: 'center' });
      
      doc.setFont('helvetica', 'italic');
      doc.text('Keep this QR code private - it contains your personal registration link', pageWidth / 2, footerY - 5, { align: 'center' });
      
      // Save the PDF with the patient's name
      doc.save(`${patientName.replace(/\s+/g, '_')}_App_Registration.pdf`);
      
      // Call the onGenerate callback if provided
      if (onGenerate) {
        onGenerate();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  return (
    <button
      onClick={generatePDF}
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
    >
      Generate PDF Instructions
    </button>
  );
};